import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { SITE_VERSION } from "@/lib/version";
import { LayoutDashboard, Package, ClipboardList, PenLine, Settings, LogOut, Loader2, MessageSquareHeart, Type, Tags, UserRound, Ticket } from "lucide-react";
import { toast } from "sonner";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/produits", label: "Produits & Stock", icon: Package, testid: "admin-nav-products" },
  { to: "/admin/commandes", label: "Commandes", icon: ClipboardList, testid: "admin-nav-orders" },
  { to: "/admin/journal", label: "Conseils", icon: PenLine, testid: "admin-nav-blog" },
  { to: "/admin/promos", label: "Codes promo", icon: Ticket, testid: "admin-nav-promos" },
  { to: "/admin/avis", label: "Avis clientes", icon: MessageSquareHeart, testid: "admin-nav-testimonials" },
  { to: "/admin/textes", label: "Textes du site", icon: Type, testid: "admin-nav-textes" },
  { to: "/admin/categories", label: "Catégories", icon: Tags, testid: "admin-nav-categories" },
  { to: "/admin/compte", label: "Mon compte", icon: UserRound, testid: "admin-nav-compte" },
  { to: "/admin/reglages", label: "Réglages", icon: Settings, testid: "admin-nav-settings" },
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (forgot) {
      try {
        const { data } = await api.post("/auth/forgot-password", { email });
        setSent(data.message);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light flex items-center justify-center px-6">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl border border-line p-8 md:p-10 shadow-[0_20px_60px_rgba(16,114,105,0.1)]">
        <div className="mb-8">
          <img src="/logo.png" alt="Tendres Tétées" className="h-14 w-auto" />
          <p className="mt-3 text-xs text-mauve font-mono uppercase tracking-widest">Espace gérante</p>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="text-sm font-medium text-ink">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
            />
          </div>
          {!forgot && (
            <div>
              <label htmlFor="admin-password" className="text-sm font-medium text-ink">Mot de passe</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
              />
            </div>
          )}
          {error && <p data-testid="admin-login-error" className="text-sm text-[#b04a4a] bg-[#b04a4a]/10 rounded-xl px-4 py-2.5">{error}</p>}
          {sent && <p data-testid="admin-forgot-sent" className="text-sm text-teal-dark bg-mint/25 rounded-xl px-4 py-2.5">{sent}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? (forgot ? "Envoi…" : "Connexion…") : forgot ? "Recevoir le lien par email" : "Se connecter"}
          </button>
          <button
            type="button"
            data-testid="admin-forgot-toggle"
            onClick={() => { setForgot((f) => !f); setError(""); setSent(""); }}
            className="w-full text-center text-sm text-mauve hover:text-teal transition-colors"
          >
            {forgot ? "← Retour à la connexion" : "Mot de passe oublié ?"}
          </button>
          <p data-testid="admin-login-version" className="pt-2 text-center font-mono text-[10px] uppercase tracking-widest text-mauve/70">Version du site : {SITE_VERSION}</p>
        </div>
      </form>
    </div>
  );
}

export default function AdminLayout() {
  const [user, setUser] = useState(undefined); // undefined = checking, null = déconnecté
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-teal" />
      </div>
    );
  }

  if (user === null) {
    return <Login onLogin={setUser} />;
  }

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed, clearing session locally", err);
    }
    setUser(null);
    navigate("/admin");
    toast.success("À bientôt !");
  };

  return (
    <AdminCtx.Provider value={{ user, setUser }}>
      <div data-testid="admin-dashboard" className="min-h-screen bg-cream-light flex">
        <aside className="w-16 md:w-64 bg-teal-deep text-cream flex flex-col fixed inset-y-0 left-0 z-40">
          <div className="px-3 md:px-6 py-4 border-b border-cream/10 shrink-0">
            <img src="/logo-light.png" alt="Tendres Tétées" className="hidden md:block h-12 w-auto" />
            <img src="/icon-512.png" alt="Tendres Tétées" className="md:hidden h-9 w-9 mx-auto" />
            <p className="hidden md:block text-[10px] font-mono uppercase tracking-widest text-cream/50 mt-1">Back office</p>
          </div>
          <nav data-testid="admin-nav" className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 p-2 md:p-3">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-2xl px-3 md:px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-cream text-teal-dark font-medium" : "text-cream/80 hover:bg-cream/10"}`
                  }
                >
                  <Icon size={18} className="shrink-0" />
                  <span className="hidden md:inline">{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <button data-testid="admin-logout" onClick={logout} className="shrink-0 flex items-center gap-3 px-4 md:px-6 py-4 text-sm text-cream/60 hover:text-rose transition-colors border-t border-cream/10">
            <LogOut size={18} />
            <span className="hidden md:inline">Déconnexion</span>
          </button>
          <p data-testid="admin-site-version" className="shrink-0 hidden md:block px-6 pb-3 font-mono text-[10px] uppercase tracking-widest text-cream/40">Version du site : {SITE_VERSION}</p>
        </aside>
        <main className="flex-1 ml-16 md:ml-64 p-4 md:p-10">
          <Outlet />
        </main>
      </div>
    </AdminCtx.Provider>
  );
}
